import React from "react"
import cx from "classnames"
import { Icon } from "../icon"
import {
  BADGE_ICONS,
  isPaidBadgeHidden,
  type NavigationBadgeType
} from "./navigation-badge"
import { useNavigationContext } from "./navigation-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationTooltipProps = {
  /** Heading line at the top of the tooltip body. */
  label?: string
  /** Description line beneath the label (or top line if no label). */
  description?: React.ReactNode
  /** Paid/beta callout rendered at the bottom. */
  badge?: NavigationBadgeType
}

// =============================================================================
// Config
// =============================================================================

const CALLOUTS: Record<
  NavigationBadgeType,
  { label: string; className: string }
> = {
  paid: { label: "Paid feature", className: "text-blue-400" },
  beta: { label: "Beta feature", className: "text-purple-400" }
}

// =============================================================================
// Component
// =============================================================================

/**
 * Structured tooltip body for nav rows. Renders up to three stacked lines —
 * label, description, and paid/beta callout — any of which may be omitted.
 * The paid callout is hidden for paid accounts (see `isPaidBadgeHidden`).
 *
 * Internal — composed by `NavigationLink` when the tooltip needs more than
 * a plain string. Not part of the public API.
 */
export function NavigationTooltip({
  label,
  description,
  badge
}: NavigationTooltipProps) {
  const { collapsed, accountType } = useNavigationContext()
  const callout =
    badge && !isPaidBadgeHidden(badge, accountType) ? CALLOUTS[badge] : null
  const hasHeading = Boolean(collapsed && label)

  return (
    <div className="flex flex-col gap-1">
      {collapsed && label && (
        <span className="font-semibold text-white">{label}</span>
      )}
      {description && (
        <span className={cx(hasHeading && "font-normal text-gray-200")}>
          {description}
        </span>
      )}
      {callout && badge && (
        <span
          className={cx(
            "flex items-center gap-1 font-semibold",
            callout.className
          )}
        >
          <Icon
            name={BADGE_ICONS[badge]}
            // eslint-disable-next-line rulesdir/no-deprecated-icon-size
            size="sm"
            aria-hidden
          />
          {callout.label}
        </span>
      )}
    </div>
  )
}

NavigationTooltip.displayName = "NavigationTooltip"
